import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { NotificationResponsePaginatedResponse } from '@/api/schema/data-contracts';
import { notificationsRefetchIntervalInMiliseconds } from '@/constants';
import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type { MutationProps, NotificationListParams } from '../types';

// Prefix shared by every notifications query, regardless of pagination/sort params.
// Used for fuzzy cache targeting so we never depend on a single concrete key.
const NOTIFICATIONS_QUERY_PREFIX = ['notifications'] as const;

export const useNotificationQueryActions = () => {
  const { auth } = useAuth();
  const queryClient = useQueryClient();

  const {
    notificationsController: { v1NotificationsList, v1NotificationsUpdate },
  } = useApi();

  const useGetNotifications = (params?: NotificationListParams) =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: [...NOTIFICATIONS_QUERY_PREFIX, params],
      queryFn: async () => {
        const { data } = await v1NotificationsList(params);
        return data;
      },
      // Poll while the tab is actively focused; the interval pauses when the tab
      // is backgrounded (default), so we don't waste requests on idle visitors.
      refetchInterval: notificationsRefetchIntervalInMiliseconds,
      // When the visitor returns to the tab, refetch — but only if the list is
      // older than the refresh window. Overrides the global refetchOnWindowFocus:false;
      // staleTime gates it so focusing within 2 min doesn't trigger a needless fetch (EWM-635).
      refetchOnWindowFocus: true,
      staleTime: notificationsRefetchIntervalInMiliseconds,
    });

  const useReadNotification = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['read-notification'],
      mutationFn: async (notificationId: string) => {
        const { data } = await v1NotificationsUpdate(notificationId, { isRead: true });
        return data;
      },
      onSuccess: (_, notificationId) => {
        // Optimistically flip the notification to read in EVERY cached notifications
        // query (the header bell and the /notifications Timeline share the data but may
        // hold different param keys, and only the fetching instance's key is concrete).
        // Fuzzy-matching on the prefix updates them all without depending on a single key.
        queryClient.setQueriesData<NotificationResponsePaginatedResponse>(
          { queryKey: NOTIFICATIONS_QUERY_PREFIX },
          (cachedData) =>
            cachedData
              ? {
                  ...cachedData,
                  items: cachedData.items.map((item) =>
                    item.id === notificationId ? { ...item, isRead: true } : item,
                  ),
                }
              : cachedData,
        );

        // Reconcile with the server (also covers the unread badge count).
        void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_PREFIX });
      },
    });

  return { useGetNotifications, useReadNotification };
};
